import { memo } from 'react';

import { ChevronDownIcon } from '@air/design-icons';
import { cn, tailwindVariants } from '@air/utils-tailwind';

import { VersionChip, type VersionChipProps } from '~/components/AssetModalV2/components/VersionChip';

const currentVersionChipVariants = tailwindVariants({
  slots: {
    chip: 'flex',
    currentLabel: '',
    dropdownIndicator: 'shrink-0',
    separator: 'px-px',
  },
  variants: {
    color: {
      blue: {
        currentLabel: 'text-white',
        dropdownIndicator: 'text-white',
        separator: 'text-white/70',
      },
      grey: {
        chip: 'ads:bg-ads-gray-6 bg-gray-6',
        currentLabel: 'ads:text-secondary text-gray-11',
        dropdownIndicator: 'ads:text-icon-default text-gray-11',
        separator: 'ads:text-metadata text-gray-9',
      },
    },
  },
});

export type CurrentVersionChipProps = Pick<VersionChipProps, 'className' | 'color' | 'data-testid'> & {
  isHeadVersion?: boolean;
  showDropdownIndicator?: boolean;
  versionNumber: number;
};

export const CurrentVersionChip = memo(
  ({
    className,
    color = 'grey',
    'data-testid': dataTestId,
    isHeadVersion = false,
    showDropdownIndicator = false,
    versionNumber,
  }: CurrentVersionChipProps) => {
    const { chip, currentLabel, dropdownIndicator, separator } = currentVersionChipVariants({
      color: color === 'blue' ? 'blue' : 'grey',
    });

    return (
      <VersionChip
        className={cn(chip(), showDropdownIndicator && 'gap-0.5', className)}
        color={color}
        data-testid={dataTestId}
      >
        <span className="flex items-center gap-0.5 whitespace-nowrap text-12">
          <span>V{versionNumber}</span>
          {isHeadVersion ? (
            <>
              <span aria-hidden className={separator()}>
                ·
              </span>
              <span className={currentLabel()}>Current</span>
            </>
          ) : null}
        </span>
        {showDropdownIndicator ? <ChevronDownIcon aria-hidden size="xs" className={dropdownIndicator()} /> : null}
      </VersionChip>
    );
  },
);

CurrentVersionChip.displayName = 'CurrentVersionChip';
